import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AuthService from '../services/authService';

// Interface for team options from the API
interface TeamOption {
  id: number;
  name: string;
}

// Interface for form errors
interface FormError {
  UserName?: string;
  Email?: string;
  fullName?: string;
  TeamId?: string;
  designation?: string;
  General?: string;
}

// Interface for the props of the main form
interface CreateUserFormProps {
  handleCreateUser: (formData: any) => Promise<boolean | void>;
  username: string;
  setUsername: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  isPending?: boolean;
  error: FormError;
  setError: (error: FormError) => void;
  switchToLogin: () => void;
}

// Simple Spinner component
const Spinner: React.FC<{size?: string}> = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };
  
  return (
    <div className="flex justify-center items-center">
      <div className={`animate-spin rounded-full border-t-2 border-b-2 border-blue-500 ${sizeClasses[size as keyof typeof sizeClasses]}`}></div>
    </div>
  );
};

// Generic Searchable Dropdown Component
interface SearchableDropdownProps<T> {
  id: string;
  options: T[];
  optionDisplayKey: keyof T;
  optionValueKey: keyof T;
  placeholder: string;
  label: string;
  selectedOption: T | null;
  onSelectionChange: (option: T | null) => void;
  isLoading?: boolean;
  error?: string;
  required?: boolean;
}

function SearchableDropdown<T extends Record<string, any>>({
  id,
  options,
  optionDisplayKey,
  optionValueKey,
  placeholder,
  label,
  selectedOption,
  onSelectionChange,
  isLoading = false,
  error,
  required = false
}: SearchableDropdownProps<T>) {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<T[]>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update filtered options when search text changes or options change
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => 
        String(option[optionDisplayKey]).toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchText, options, optionDisplayKey]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    
    if (value === '' && selectedOption) {
      onSelectionChange(null);
    }
  };

  // Handle option selection
  const handleOptionClick = (option: T) => {
    setSearchText(String(option[optionDisplayKey]));
    onSelectionChange(option);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // CSS classes
  const inputClasses = `w-full p-2 border rounded-md ${error ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-cyan-500`;
  const labelClasses = `block text-sm font-medium text-gray-700 mb-1 ${required ? 'after:content-["*"] after:ml-0.5 after:text-red-500' : ''}`;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label htmlFor={id} className={labelClasses}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type="text"
          className={inputClasses}
          placeholder={placeholder}
          value={searchText}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        {isLoading && (
          <div className="absolute right-2 top-2">
            <Spinner size="sm" />
          </div>
        )}
      </div>
      
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      
      {isOpen && filteredOptions.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 max-h-60 overflow-auto bg-white border border-gray-300 rounded-md shadow-lg">
          {filteredOptions.map((option, index) => (
            <li
              key={String(option[optionValueKey]) + index}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleOptionClick(option)}
            >
              {String(option[optionDisplayKey])}
            </li>
          ))}
        </ul>
      )}
      
      {isOpen && filteredOptions.length === 0 && (
        <div className="absolute z-10 w-full mt-1 p-2 bg-white border border-gray-300 rounded-md shadow-lg text-gray-500">
          No matches found
        </div>
      )}
    </div>
  );
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({
  handleCreateUser,
  username,
  setUsername,
  email,
  setEmail,
  isPending = false,
  error,
  setError,
  switchToLogin
}) => {
  // State for form fields
  const [fullName, setFullName] = useState('');
  const [designation, setDesignation] = useState(''); 
  
  // State for teams
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamOption | null>(null);
  
  // Loading states
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Function to clear specific error when field is corrected
  const clearFieldError = (fieldName: keyof FormError) => {
    if (error[fieldName]) {
      setError({ ...error, [fieldName]: undefined });
    }
  };

  // Fetch teams on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      setLoadingTeams(true);
      try {
        const authService = new AuthService();
        const response = await authService.getTeams();
        // Check if response has data and data.teams is an array
        if (response && response.data && response.data.teams && Array.isArray(response.data.teams)) {
          setTeams(response.data.teams);
        } else {
          console.error('Invalid teams data format:', response);
          setError({ ...error, General: 'Failed to load teams. Invalid data format.' });
          setTeams([]); // Set empty array as fallback
        }
      } catch (err) {
        console.error('Error fetching teams:', err);
        setError({ ...error, General: 'Failed to load teams. Please try again.' });
        setTeams([]); // Set empty array as fallback
      } finally {
        setLoadingTeams(false);
      }
    };

    fetchTeams();
  }, []);

  // Handle username change
  const handleUsernameChange = (value: string) => {
    setUsername(value);
    if (value.trim()) {
      clearFieldError('UserName');
    }
  };

  // Handle email change
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value.trim() && /\S+@\S+\.\S+/.test(value)) {
      clearFieldError('Email');
    }
  };

  // Handle full name change
  const handleFullNameChange = (value: string) => {
    setFullName(value);
    if (value.trim()) {
      clearFieldError('fullName');
    }
  };

  // Handle designation change
  const handleDesignationChange = (value: string) => {
    setDesignation(value);
    if (value.trim()) {
      clearFieldError('designation');
    }
  };

  // Handle team selection change
  const handleTeamSelectionChange = (team: TeamOption | null) => {
    setSelectedTeam(team);
    // console.log("selected team is ", team);
    if (team) {
      clearFieldError('TeamId');
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: FormError = {};
    let isValid = true;

    if (!username.trim()) {
      newErrors.UserName = 'Username is required';
      isValid = false;
    }

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      newErrors.Email = 'Valid email is required';
      isValid = false;
    }

    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    if (!designation.trim()) {
      newErrors.designation = 'Designation is required';
      isValid = false;
    }

    if (!selectedTeam) {
      newErrors.TeamId = 'Please select a team';
      isValid = false;
    }

    if (!isValid) {
      setError(newErrors);
      return;
    }

    // Prepare form data for submission
    const formData = {
      UserName: username.trim(),
      Email: email.trim(),
      fullName: fullName.trim(),
      TeamId: selectedTeam?.id,
      TeamName: selectedTeam?.name,  

      designation: designation.trim()
    };

    // Submit form
    await handleCreateUser(formData);
  };

  // CSS classes
  const inputBaseClasses = "w-full p-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-500";
  const errorInputClasses = "border-red-500 focus:ring-red-500";
  const labelClasses = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="flex flex-col items-center w-full max-w-2xl bg-white p-8 sm:p-10 shadow-2xl rounded-xl">
      <div className="w-full flex flex-col">
        {/* Header */}
        <div className="flex flex-col items-center w-full mb-8">
          <img loading="lazy" srcSet="/athlyte_logo.png" className="w-20 h-auto mb-3" alt="Athlyte Logo" />
          <h1 className="text-2xl font-bold text-gray-800">Create Your Athlyte Account</h1>
          <p className="text-gray-600 text-center mt-2">
            Join the community of athletes, coaches, and fans
          </p>
        </div>

        {/* General error message */}
        {error.General && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error.General}
          </div>
        )}

        {/* Form fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          {/* Team */}
          <div>
            <SearchableDropdown
              id="team"
              options={teams}
              optionDisplayKey="name"
              optionValueKey="id"
              placeholder="Search for your Team"
              label="Team"
              selectedOption={selectedTeam}
              onSelectionChange={handleTeamSelectionChange}
              isLoading={loadingTeams}
              error={error.TeamId}
              required={true}
            />
          </div>

          {/* Team request link */}
          <div className="col-span-1 md:col-span-2 -mt-2 mb-2">
            <p className="text-sm text-gray-500">
              <Link to="/request-team" className="text-blue-600 hover:text-blue-800 underline"> Don't see your Team?</Link>
            </p>
          </div>
          
          {/* Username */}
          <div>
            <label htmlFor="username" className={`${labelClasses} after:content-["*"] after:ml-0.5 after:text-red-500`}>
              Username
            </label>
            <input
              id="username"
              type="text"
              placeholder="Choose a username"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              className={`${inputBaseClasses} ${error.UserName ? errorInputClasses : ""}`}
            />
            {error.UserName && <p className="mt-1 text-xs text-red-500">{error.UserName}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className={`${labelClasses} after:content-["*"] after:ml-0.5 after:text-red-500`}>
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={`${inputBaseClasses} ${error.Email ? errorInputClasses : ""}`}
            />
            {error.Email && <p className="mt-1 text-xs text-red-500">{error.Email}</p>}
          </div>

          {/* Full Name */}
          <div>
            <label htmlFor="fullname" className={`${labelClasses} after:content-["*"] after:ml-0.5 after:text-red-500`}>
              Full Name
            </label>
            <input
              id="fullname"
              type="text"
              placeholder="Your full name"
              value={fullName}
              onChange={(e) => handleFullNameChange(e.target.value)}
              className={`${inputBaseClasses} ${error.fullName ? errorInputClasses : ""}`}
            />
            {error.fullName && <p className="mt-1 text-xs text-red-500">{error.fullName}</p>}
          </div>

          {/* Designation - Now mandatory */}
          <div>
            <label htmlFor="designation" className={`${labelClasses} after:content-["*"] after:ml-0.5 after:text-red-500`}>
              Designation
            </label>
            <input
              id="designation"
              type="text"
              placeholder="Your designation (e.g., coach, athlete)"
              value={designation}
              onChange={(e) => handleDesignationChange(e.target.value)}
              className={`${inputBaseClasses} ${error.designation ? errorInputClasses : ""}`}
            />
            {error.designation && <p className="mt-1 text-xs text-red-500">{error.designation}</p>}
          </div>
        </div>

        {/* Submit button */}
        <div className="mt-8 flex flex-col items-center">
          <button
            type="submit"
            disabled={isPending}
            className="w-full py-2 px-4 bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            {isPending ? (
              <div className="flex justify-center items-center">
                <Spinner size="sm" />
                <span className="ml-2">Creating account...</span>
              </div>
            ) : (
              'Create Account'
            )}
          </button>
          
          <div className="mt-4 text-sm">
            Already have an account?{' '}
            <button
              type="button"
              onClick={switchToLogin}
              className="text-cyan-600 hover:text-cyan-800 font-medium"
            >
              Log in
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CreateUserForm;